<template>
  <div v-if="blocks" :class="['alinea', alineaVariant, alineaStyle]">
    <template v-for="block in blocks" :key="block.id">
      <Heading
        v-if="block.type === 'header'"
        class="alinea-heading"
        :content="block.data.text as string"
        :level="headingLevel"
      />
      <Paragraph
        v-if="block.type === 'paragraph'"
        class="alinea-paragraph"
        :content="block.data.text"
      />
      <List
        v-if="block.type === 'nestedlist'"
        :unordered="block.data.style === 'unordered'"
        class="alinea-list"
        :items="block.data.items"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import type { ITextBlock, TTextContent, TTextStyle } from '~~/types/local_types';

interface IProps {
  blocks?: Array<ITextBlock>;
  variant?: TTextContent;
  textStyle?: TTextStyle;
  headingLevel?: number;
}

const props = withDefaults(defineProps<IProps>(), {
  blocks: undefined,
  variant: 'body',
  textStyle: 'regular',
  headingLevel: 3,
});

const alineaVariant = `is--${props.variant}`;
const alineaStyle = `is--${props.textStyle}`;
</script>

<style lang="postcss" scoped>
@define-mixin inlineIconsBefore {
  content: '';
  position: absolute;
  display: inline-block;
  left: 0;
  border-radius: var(--unit-4);
  top: var(--alinea-link-icon-offset-top);
  height: var(--unit-24);
  width: var(--unit-24);
  background-color: hsl(var(--color-secondary-500));
  transition: background-color 150ms ease-in-out;
}

@define-mixin inlineIcons {
  position: absolute;
  width: var(--alinea-link-icon-size);
  height: var(--alinea-link-icon-size);
  left: var(--alinea-link-icon-margin-left);
  top: 0;
  background-repeat: no-repeat;
  background-position: center center;
  content: '';
  background-color: hsl(var(--color-neutral-white));
  transition: background-color 150ms ease-in-out;
}

.alinea {
  --alinea-link-icon-size: var(--unit-22);
  --alinea-link-icon-margin-left: var(--unit-1);
  --alinea-link-icon-offset-top: calc(var(--unit-1) * -1);
  --alinea-link-hover-color: hsl(var(--color-secondary-700));
  --icon-left: var(--unit-2);

  @media (--lg-viewport) {
    --alinea-link-icon-size: var(--unit-24);
    --alinea-link-icon-margin-left: 0;
    --alinea-link-icon-offset-top: 0;
    --icon-left: var(--unit-4);
  }
}

.is--regular {
  color: hsl(var(--color-primary-900));
}

.is--inverted {
  color: hsl(var(--color-neutral-white));
}

.alinea-paragraph,
.alinea-list {
  .is--teaser & {
    @mixin title-md {
    }
  }

  .is--intro & {
    @mixin body-lg {
    }

    &:deep(b) {
      font-weight: var(--font-weight-bold);
    }
  }

  .is--rte &,
  .is--body & {
    @mixin body-md {
    }

    &:deep(b) {
      font-weight: var(--font-weight-semi-bold);
    }
  }
}

.alinea-paragraph,
.alinea-heading,
.alinea-list {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;

  .is--rte &,
  .is--body &,
  .is--intro & {
    &:first-child {
      margin-top: 0;
    }
    margin-top: var(--unit-18);
  }

  @media (--lg-viewport) {
    margin-top: var(--unit-24);
  }
}

.alinea-list {
  margin-left: var(--unit-12);
}

.alinea-heading + .alinea-paragraph {
  margin-top: 9px;

  @media (--lg-viewport) {
    margin-top: var(--unit-12);
  }
}

.alinea-heading {
  .is--body &,
  .is--intro & {
    @mixin heading-lg {
    }
  }

  .is--rte & {
    @mixin title-lg {
    }
  }
}

:deep(.alinea-paragraph),
:deep(.alinea-list),
:deep(.alinea-heading) {
  a[href] {
    color: hsl(var(--color-secondary-500));
    position: relative;
    font-weight: var(--font-weight-semi-bold);
    display: inline;
    align-items: center;
    transition: color 150ms ease-in-out;
    text-decoration: underline;
    padding-left: calc(var(--unit-24) + var(--icon-left));

    &.no-icon {
      &::after {
        content: unset;
        display: none;
      }
    }

    @media (--lg-viewport) {
      @media (hover: hover) and (pointer: fine) {
        /* when hover is supported and using a mouse */
        &:hover {
          color: var(--alinea-link-hover-color);

          &::before {
            background-color: var(--alinea-link-hover-color);
          }
        }
      }
    }
  }

  a[target='_blank'] {
    &::before {
      @mixin inlineIconsBefore {
      }
    }
    &::after {
      @mixin inlineIcons;
      mask: var(--icon-external);
      mask-size: 65%;
      mask-repeat: no-repeat;
      mask-position: 50% 40%;

      @media (--lg-viewport) {
        mask-size: 65%;
        mask-repeat: no-repeat;
        mask-position: 45% center;
      }
    }
  }
  a[href*='facebook'] {
    &::before {
      @mixin inlineIconsBefore {
      }
    }
    &::after {
      @mixin inlineIcons;
      mask: var(--icon-facebook);
      mask-size: 110%;
      mask-repeat: no-repeat;
      mask-position: center center;
    }
  }
  a[href*='instagram'] {
    &::before {
      @mixin inlineIconsBefore {
      }
    }
    &::after {
      @mixin inlineIcons;
      mask: var(--icon-instagram);
      mask-size: 90%;
      mask-repeat: no-repeat;
      mask-position: 20% 20%;
    }
  }
  a[href*='youtube'] {
    &::before {
      @mixin inlineIconsBefore {
      }
    }
    &::after {
      @mixin inlineIcons;
      mask: var(--icon-youtube);
      mask-size: 100%;
      mask-repeat: no-repeat;
      mask-position: center center;
    }
  }
  a[href*='tiktok'] {
    &::before {
      @mixin inlineIconsBefore {
      }
    }
    &::after {
      @mixin inlineIcons;
      mask: var(--icon-tiktok);
      mask-size: 70%;
      mask-repeat: no-repeat;
      mask-position: center center;
    }
  }
  a[href*='threads'] {
    &::before {
      @mixin inlineIconsBefore {
      }
    }
    &::after {
      @mixin inlineIcons;
      mask: var(--icon-threads);
      mask-size: 55%;
      mask-repeat: no-repeat;
      mask-position: 40% center;
    }
  }

  a[href*='bsky'] {
    &::before {
      @mixin inlineIconsBefore {
      }
    }
    &::after {
      @mixin inlineIcons;
      mask: var(--icon-bluesky);
      mask-size: 55%;
      mask-repeat: no-repeat;
      mask-position: 55% center;
    }
  }
  a[href*='eenvandaag.avrotros.nl'],
  a[href*='eenvandaag.avrotros.site'],
  a[href*='eenvandaag.dev.avrotros.site'],
  a[href*='eenvandaag.loc.avrotros.site'],
  a[href^='mailto:'],
  a[href^='/'],
  a[href^='#'] {
    display: inline;
    padding-left: 0;

    &::before {
      display: none;
    }
    &::after {
      display: none;
    }
  }
}
</style>
